// WP-1.11 (M16 DB-1..5, S-05 `/`): dashboard query.
import type { CurrencyCode } from '@pfos/engine';
import {
  consolidatedNetWorthResponseSchema,
  dashboardResponseSchema,
  type ConsolidatedNetWorthResponse,
  type DashboardResponse,
} from '@pfos/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from './api';

export function useDashboard(
  currency: CurrencyCode,
  period?: { from: string; to: string },
): UseQueryResult<DashboardResponse> {
  const params = new URLSearchParams({ currency });
  if (period !== undefined) {
    params.set('from', period.from);
    params.set('to', period.to);
  }
  return useQuery({
    queryKey: ['dashboard', currency, period],
    queryFn: async () =>
      dashboardResponseSchema.parse(await api.get(`/api/v1/dashboard?${params.toString()}`)),
  });
}

/** AC-5: consolidated cross-currency net worth in `baseCurrency`. `enabled` lets the caller
 * skip the fetch entirely when the owner only holds one currency — nothing to consolidate,
 * so there's no reason to ask. */
export function useConsolidatedNetWorth(
  baseCurrency: CurrencyCode,
  enabled: boolean,
): UseQueryResult<ConsolidatedNetWorthResponse> {
  return useQuery({
    queryKey: ['dashboard-consolidated', baseCurrency],
    enabled,
    queryFn: async () =>
      consolidatedNetWorthResponseSchema.parse(
        await api.get(`/api/v1/dashboard/consolidated?baseCurrency=${baseCurrency}`),
      ),
  });
}
